/**
 * @fileoverview System-side Customer Portal defaults page — mounts the shared
 *               ui-admin PortalConfigEditor against the system-defaults doc.
 * @module system-web/pages/PortalDefaultsPage
 *
 * Responsibilities:
 * - Render the shared config editor for the 'system-defaults' portal config —
 *   the baseline seeded into newly provisioned tenants.
 *
 * Dependencies: @firesquirrel/ui-admin (PortalConfigEditor), usePortalConfig.
 *
 * @component PortalDefaultsPage
 * Purpose:     System-admin portal defaults surface (/portal-config).
 * Props:       none — route component.
 * Behavior:    liveQuery-cached config; save goes through
 *              portalConfigService (system-api boundary — the only place
 *              paymentGateway refs may be written).
 * Data Source: hybrid — Dexie cache + online save.
 * Constraints: per-tenant config is edited via tenant-api; this page is
 *   defaults-only. Template catalog is not bound here (templates are
 *   tenant-scoped — the defaults doc stores workflow *shape* only).
 */
import { PortalConfigEditor, type PortalConfigValue } from '@firesquirrel/ui-admin';
import { usePortalConfig } from '../hooks/usePortalConfig';
import { savePortalDefaults } from '../services/portalConfigService';

export default function PortalDefaultsPage() {
  const { config, isLoading, error } = usePortalConfig();

  if (isLoading) return <p className="p-6 text-sm text-gray-500">Loading…</p>;

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <h1 className="text-2xl font-semibold text-gray-900">Customer Portal — System Defaults</h1>
      <p className="text-sm text-gray-500">
        Baseline portal configuration seeded into newly provisioned tenants.
        Tenant-specific overrides are managed inside each department's tenant-web.
      </p>
      {error && <p className="rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      <PortalConfigEditor
        value={config}
        templates={[]}
        onSave={(next: PortalConfigValue) => savePortalDefaults(next).then(() => undefined)}
      />
    </div>
  );
}
